import { Controller } from 'stimulus'
import trackBusy from '../lib/metrics/busy'
import { renderTraitChips, describeTraitFilters } from '../lib/metrics/trait_chips'
import {
  createSearchList, fieldChips, renderFilterChips, stateFromTokens, stateToTokens,
  isEmptyState, matchesTerm, normalizeTerm, CONTAINS_PREFIX
} from '../lib/metrics/search_list'
import renderEmptyNote, { rangePhrase } from '../lib/metrics/empty_note'
import JobChipFilters from '../lib/metrics/job_chips'
import {
  stackedColumns, hBars, kpiTile, renderTable, fmtNum, fmtDate,
  groupedColumns, multiLine, hBarsStacked, SLOTS
} from '../lib/metrics/charts'

const GROUP_NAMES = ['Group A', 'Group B', 'Group C']

const STATUS_COLORS = {
  Pass: 'var(--m-st-pass)',
  Fail: 'var(--m-st-fail)',
  Completed: 'var(--m-st-completed)',
  'Not tested': 'var(--m-st-nt)',
  Other: 'var(--m-st-other)'
}

// Drives the Tasks & Results tab. Lazy-loads on first tab activation
// (metrics:results-shown, dispatched by the metrics controller).
export default class extends Controller {
  static targets = [
    'range', 'taskSearch', 'traitPicker', 'valueSearch', 'chips',
    'jobFilterPicker', 'jobValueSearch', 'emptyNote',
    'kpiResults', 'kpiPassRate', 'kpiFails', 'kpiLast',
    'weeklyChart', 'weeklyLegend', 'weeklyTable',
    'hotChart', 'hotTable', 'hotSub', 'byTask',
    'compareBtn', 'groupRows', 'compareGrid', 'singleGrid',
    'cmpTiles', 'cmpLegend', 'cmpWeekly', 'cmpPass', 'cmpMix'
  ];

  connect() {
    // trait value filters by trait key (normalized name), in the order
    // added: { traitName, terms, picks, excludes, termTotals }
    this.traitStates = new Map()
    // Job filter chips (tag / job column / job field; lib/metrics/job_chips):
    // value lists count jobs with results in the range under the task and
    // trait filters (scope=results)
    this.jobChips = new JobChipFilters({
      picker: this.jobFilterPickerTarget,
      anchor: this.jobValueSearchTarget,
      urls: {
        tag: this.data.get('tagValuesUrl'),
        column: this.data.get('columnValuesUrl'),
        field: this.data.get('fieldValuesUrl')
      },
      scope: 'results',
      baseParams: () => {
        const p = new URLSearchParams({ range: this.rangeTarget.value })
        this.appendTaskParams(p)
        this.appendTraitParams(p)
        return p
      },
      onChange: () => {
        this.renderChips()
        this.load()
      },
      onLabels: () => this.renderChips()
    })
    this.groups = []
    // display labels by normalized task key (search list rows, By task rows)
    this.taskLabels = new Map()
    // Task search list: "All containing" terms, exact names, and left-out
    // names. Its excludes ARE exclude_task[], shared with the By task
    // card's checkboxes.
    this.taskList = createSearchList(this.taskSearchTarget, {
      label: 'Task',
      placeholder: 'All tasks',
      searchPlaceholder: 'Search tasks',
      nounOne: 'name',
      nounMany: 'names',
      countNoun: 'results',
      labels: this.taskLabels,
      fetchOptions: (q, state) => this.fetchTaskOptions(q, state),
      onApply: () => {
        this.renderChips()
        this.load()
      },
      onLabels: () => {
        this.renderChips()
        if (this.lastByTask) this.renderByTask(this.lastByTask)
      }
    })
    this.renderValueList(false)
    this.initFromUrl()
    // restored picks and exclusions need display names
    this.taskList.ensureLabels()
    this.jobChips.ensureLabels()
    // reload on every show: cheap behind the server-side response cache, and
    // charts re-measure — widths captured while hidden or at another window
    // size would otherwise stick
    this.onShow = () => this.load()
    window.addEventListener('metrics:results-shown', this.onShow)
    // Direct ?tab=results navigation (saved views, shared URLs): the outer
    // metrics controller activates this panel during ITS connect(), which
    // runs before this listener registers — the shown event is already
    // gone. The active class survives, so load from it.
    if (this.element.classList.contains('active')) this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.element.offsetParent !== null) this.load()
      }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  // Honor filters arriving via URL (saved-view links, shared URLs)
  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if (url.get('tab') !== 'results') return
    if (url.get('range')) this.rangeTarget.value = url.get('range')
    // task[]= (multi-select / search list) or the older scalar task=
    // (saved views and shared links from before multi-select). Exact names
    // are normalized keys (the server lower-cases them too).
    const taskKey = t => (String(t).startsWith(CONTAINS_PREFIX) ? t : String(t).trim().toLowerCase())
    const tasks = url.getAll('task[]').concat(url.getAll('task')).map(taskKey)
    const excluded = url.getAll('exclude_task[]').concat(url.getAll('exclude_task')).map(taskKey)
    if (tasks.length || excluded.length) this.taskList.setState(stateFromTokens(tasks, excluded))
    // traits[]=<name>:<value> or <name>:~<words>; exclude_traits[]=<name>:<value>
    const eachTrait = (param, fn) => url.getAll(param).forEach((raw) => {
      const [rawKey, value] = raw.split(/:(.*)/s)
      const traitId = decodeURIComponent(rawKey || '')
      if (traitId && value) fn(this.traitState(traitId), value)
    })
    eachTrait('traits[]', (st, value) => {
      if (value.startsWith(CONTAINS_PREFIX)) st.terms.push(normalizeTerm(value.slice(CONTAINS_PREFIX.length)))
      else st.picks.push(value)
    })
    eachTrait('exclude_traits[]', (st, value) => st.excludes.push(value))
    // tags[] / columns[] / fields[]: exact values or ~terms
    this.jobChips.initFromUrl(url)
    this.renderChips()
    const rawGroups = url.getAll('groups[]')
    if (rawGroups.length >= 2) {
      this.groups = rawGroups.slice(0, 3).map((raw, slot) => ({
        slot,
        filters: raw.split('|').filter(Boolean).map((pair) => {
          const [rawKey, value] = pair.split(/:(.*)/s)
          const traitId = decodeURIComponent(rawKey || '')
          const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
          return { traitId, traitName: opt ? opt.textContent : traitId, value }
        })
      }))
      this.enterCompare()
    }
  }

  // Captured into saved views (page_name metrics#show).
  viewParams() {
    const params = { tab: 'results', range: this.rangeTarget.value }
    const tasks = stateToTokens(this.taskList.getState())
    if (tasks.length) params.task = tasks
    if (this.excludedTasks.length) params.exclude_task = this.excludedTasks.slice()
    const traits = new URLSearchParams()
    this.appendTraitParams(traits)
    if (traits.getAll('traits[]').length) params.traits = traits.getAll('traits[]')
    if (traits.getAll('exclude_traits[]').length) params.exclude_traits = traits.getAll('exclude_traits[]')
    this.jobChips.viewParams(params)
    if (this.compareActive()) {
      params.groups = this.groups.map(g => g.filters.map(f => `${encodeURIComponent(f.traitId)}:${f.value}`).join('|'))
    }
    return params
  }

  disconnect() {
    window.removeEventListener('metrics:results-shown', this.onShow)
    window.removeEventListener('resize', this.resizeHandler)
    // Turbolinks caches the page: drop the popovers and their document
    // listeners (connect() rebuilds the controls from the anchors)
    this.taskList.destroy()
    if (this.valueList) this.valueList.destroy()
    this.jobChips.destroy()
  }

  // exclude_task[] keys: names left out of every card
  get excludedTasks() {
    return this.taskList.getState().excludes
  }

  // task[] plus exclude_task[]: every card and compare group gets both
  appendTaskParams(p) {
    const state = this.taskList.getState()
    stateToTokens(state).forEach(t => p.append('task[]', t))
    state.excludes.forEach(t => p.append('exclude_task[]', t))
  }

  // traits[] (picks and ~terms) and exclude_traits[]; except: a trait key
  // left out (its own value list counts without its own filter)
  appendTraitParams(p, except = null) {
    this.traitStates.forEach((st, traitId) => {
      if (traitId === except) return
      const k = encodeURIComponent(traitId)
      st.terms.forEach(t => p.append('traits[]', `${k}:${CONTAINS_PREFIX}${t}`))
      st.picks.forEach(v => p.append('traits[]', `${k}:${v}`))
      st.excludes.forEach(v => p.append('exclude_traits[]', `${k}:${v}`))
    })
  }

  queryString() {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    this.appendTaskParams(p)
    this.appendTraitParams(p)
    this.appendJobChipParams(p)
    return p.toString()
  }

  optionsFetch(url, p) {
    return fetch(`${url}?${p.toString()}`, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
  }

  // Task search list rows: names with results in range, counted under the
  // trait and job filters (not the task filter itself)
  fetchTaskOptions(q, state) {
    const p = new URLSearchParams({ range: this.rangeTarget.value, list: '1' })
    if (q) p.set('q', q)
    state.picks.concat(state.excludes).forEach(v => p.append('selected[]', v))
    this.appendTraitParams(p)
    this.appendJobChipParams(p)
    return this.optionsFetch(this.data.get('tasksUrl'), p)
  }

  // Trait value rows: counted under every other filter, not this trait's
  fetchTraitValueOptions(traitId, q, state) {
    const p = new URLSearchParams({ trait: traitId, range: this.rangeTarget.value, list: '1' })
    if (q) p.set('q', q)
    state.picks.concat(state.excludes).forEach(v => p.append('selected[]', v))
    this.appendTaskParams(p)
    this.appendTraitParams(p, traitId)
    this.appendJobChipParams(p)
    return this.optionsFetch(this.data.get('traitValuesUrl'), p)
  }

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    // "23 of 24" counts are faceted like the lists: recount the terms
    // whenever the filters change
    const filterKey = this.queryString()
    if (filterKey !== this.lastFilterKey) {
      this.lastFilterKey = filterKey
      this.refreshTermCounts()
    }
    if (this.compareActive()) {
      renderEmptyNote(this.emptyNoteTarget, null)
      this.loadCompare()
      return
    }
    const qs = this.queryString()
    this.fetchJson(`${this.data.get('kpisUrl')}?${qs}`, data => this.renderKpis(data))
    this.fetchJson(`${this.data.get('weeklyUrl')}?${qs}`, data => this.renderWeekly(data))
    this.fetchJson(`${this.data.get('hotSpotsUrl')}?${qs}`, data => this.renderHotSpots(data))
    this.fetchJson(`${this.data.get('byTaskUrl')}?${qs}`, data => this.renderByTask(data))
  }

  // Name/value counts behind the term chips ("water burst · 23 of 24"),
  // under the current filters. Served from the server's cached option rows.
  refreshTermCounts() {
    this.taskList.forgetCounts()
    this.taskList.ensureCounts()
    this.traitStates.forEach((st, traitId) => {
      st.termTotals.clear()
      if (!st.terms.length) return
      Promise.all(st.terms.map(t => this.fetchTraitValueOptions(traitId, t, st)
        .then((data) => { st.termTotals.set(t, data.total_options || 0) })))
        .then(() => this.renderChips())
        .catch(() => {})
    })
    this.jobChips.refreshTermCounts()
  }

  // "Nothing in this range" note over the cards: says so, names the newest
  // matching result outside the range, and offers Last 12 months.
  renderRangeNote(data) {
    if (this.compareActive() || data.current.results > 0) {
      renderEmptyNote(this.emptyNoteTarget, null)
      return
    }
    const range = this.rangeTarget.value
    renderEmptyNote(this.emptyNoteTarget, {
      noun: 'results',
      rangeLabel: rangePhrase(range),
      latest: data.latest_result_at || null,
      onWiden: range === '365' ? null : () => {
        this.rangeTarget.value = '365'
        this.load()
      }
    })
  }

  // ---- comparison groups: N sets of trait filters layered on the shared
  // filters; the same endpoints are fetched once per group in parallel ----

  compareActive() {
    return this.groups.length >= 2
  }

  startCompare() {
    this.groups = [{ slot: 0, filters: [] }, { slot: 1, filters: [] }]
    this.enterCompare()
    this.load()
  }

  enterCompare() {
    this.renderGroupRows()
    this.singleGridTarget.style.display = 'none'
    this.compareGridTarget.style.display = 'grid'
    this.compareBtnTarget.style.display = 'none'
  }

  exitCompare() {
    this.groups = []
    this.groupRowsTarget.innerHTML = ''
    this.singleGridTarget.style.display = 'grid'
    this.compareGridTarget.style.display = 'none'
    this.compareBtnTarget.style.display = ''
    this.load()
  }

  groupSummary(group) {
    return describeTraitFilters(this.chipFilters(group.filters)) || 'All matching results'
  }

  // trait filters in the shape lib/metrics/trait_chips expects
  chipFilters(filters) {
    return filters.map(f => ({ ...f, label: f.traitName }))
  }

  renderGroupRows() {
    const host = this.groupRowsTarget
    host.innerHTML = ''
    this.groups.forEach((group, gi) => {
      const row = document.createElement('div')
      row.className = 'm-grp-row'
      const dot = document.createElement('span')
      dot.className = 'm-gdot'
      dot.style.background = SLOTS[group.slot]
      row.appendChild(dot)
      const name = document.createElement('span')
      name.className = 'm-gname'
      name.textContent = GROUP_NAMES[group.slot]
      row.appendChild(name)

      // two values of one trait in a group match any of them
      renderTraitChips(row, this.chipFilters(group.filters), (_, fi) => {
        group.filters.splice(fi, 1)
        this.renderGroupRows()
        this.load()
      })

      const traitSel = document.createElement('select')
      traitSel.appendChild(new Option('+ Add trait…', ''));
      [...this.traitPickerTarget.options].slice(1).forEach((o) => {
        traitSel.appendChild(new Option(o.textContent, o.value))
      })
      const valueSel = document.createElement('select')
      valueSel.appendChild(new Option('value…', ''))
      valueSel.disabled = true
      traitSel.addEventListener('change', () => {
        valueSel.innerHTML = ''
        valueSel.appendChild(new Option('Loading…', ''))
        valueSel.disabled = true
        if (!traitSel.value) return
        const q = new URLSearchParams({ trait: traitSel.value, range: this.rangeTarget.value })
        this.fetchJson(`${this.data.get('traitValuesUrl')}?${q}`, (data) => {
          valueSel.innerHTML = ''
          valueSel.appendChild(new Option('value…', ''))
          data.values.forEach(v => valueSel.appendChild(new Option(v, v)))
          valueSel.disabled = false
        }, false)
      })
      valueSel.addEventListener('change', () => {
        if (!traitSel.value || !valueSel.value) return
        if (group.filters.some(f => f.traitId === traitSel.value && f.value === valueSel.value)) return
        group.filters.push({
          traitId: traitSel.value,
          traitName: traitSel.selectedOptions[0].textContent,
          value: valueSel.value
        })
        this.renderGroupRows()
        this.load()
      })
      row.appendChild(traitSel)
      row.appendChild(valueSel)

      if (this.groups.length > 2) {
        const rm = document.createElement('button')
        rm.type = 'button'
        rm.className = 'm-btn'
        rm.textContent = '✕ Remove group'
        rm.addEventListener('click', () => {
          this.groups.splice(gi, 1)
          this.groups.forEach((g, i) => { Object.assign(g, { slot: i }) })
          this.renderGroupRows()
          this.load()
        })
        row.appendChild(rm)
      }
      host.appendChild(row)
    })

    const actions = document.createElement('div')
    actions.className = 'm-grp-actions'
    if (this.groups.length < 3) {
      const add = document.createElement('button')
      add.type = 'button'
      add.className = 'm-btn'
      add.textContent = '+ Add comparison group'
      add.addEventListener('click', () => {
        this.groups.push({ slot: this.groups.length, filters: [] })
        this.renderGroupRows()
        this.load()
      })
      actions.appendChild(add)
    }
    const exit = document.createElement('button')
    exit.type = 'button'
    exit.className = 'm-btn'
    exit.textContent = 'Exit compare'
    exit.addEventListener('click', () => this.exitCompare())
    actions.appendChild(exit)
    host.appendChild(actions)
  }

  groupQueryString(group) {
    const p = new URLSearchParams({ range: this.rangeTarget.value })
    this.appendTaskParams(p)
    this.appendTraitParams(p)
    this.appendJobChipParams(p)
    // group_traits[] AND with the shared traits[] (see ResultsController):
    // a group value of a trait the shared filter also uses narrows to the
    // overlap instead of ORing into the shared values
    group.filters.forEach(f => p.append('group_traits[]', `${encodeURIComponent(f.traitId)}:${f.value}`))
    return p.toString()
  }

  loadCompare() {
    const seq = this.loadSeq
    const fetches = this.groups.map(group => Promise.all([
      fetch(`${this.data.get('kpisUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
      fetch(`${this.data.get('weeklyUrl')}?${this.groupQueryString(group)}`, { headers: { Accept: 'application/json' } }).then(r => r.json())
    ]))
    trackBusy(this.busyEl(), Promise.all(fetches)).then((results) => {
      if (seq !== this.loadSeq) return
      this.renderCompare(results)
    }).catch(() => {})
  }

  renderCompare(results) {
    const groups = this.groups.map((group, i) => ({
      group,
      kpis: results[i][0],
      weekly: results[i][1],
      color: SLOTS[group.slot],
      name: GROUP_NAMES[group.slot]
    }))
    const { weeks } = groups[0].weekly

    const seriesSum = (weekly, label, i) => {
      const s = weekly.series.find(x => x.label === label)
      return s ? (s.values[i] || 0) : 0
    }

    // per-group pass-rate tiles
    const tiles = this.cmpTilesTarget
    tiles.innerHTML = ''
    groups.forEach((g) => {
      const tile = document.createElement('div')
      tile.className = 'm-card m-cmp-tile'
      kpiTile(tile, {
        label: `${g.name} — pass rate`,
        dot: g.color,
        value: g.kpis.current.pass_pct === null ? '—' : `${g.kpis.current.pass_pct}%`,
        delta: null,
        deltaText: null,
        sub: `${this.groupSummary(g.group)} · ${fmtNum(g.kpis.current.results)} results · ${fmtNum(g.kpis.current.fails)} fail`.slice(0, 90)
      })
      tiles.appendChild(tile)
    })

    // legend
    const legend = this.cmpLegendTarget
    legend.innerHTML = ''
    groups.forEach((g) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = 'm-swatch'
      sw.style.background = g.color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(`${g.name}: ${this.groupSummary(g.group)}`.slice(0, 60)))
      legend.appendChild(key)
    })

    // clustered weekly totals, pass rate in tooltip
    groupedColumns(this.cmpWeeklyTarget, {
      weeks,
      groups: groups.map(g => ({
        label: g.name,
        color: g.color,
        values: weeks.map((_, i) => g.weekly.series.reduce((a, s) => a + (s.values[i] || 0), 0)),
        tips: weeks.map((_, i) => {
          const pass = seriesSum(g.weekly, 'Pass', i)
          const fail = seriesSum(g.weekly, 'Fail', i)
          const tested = pass + fail
          return tested ? `${g.name} · ${Math.round((100 * pass) / tested)}% pass` : g.name
        })
      }))
    })

    // rolling 4-week pass rate
    multiLine(this.cmpPassTarget, {
      weeks,
      series: groups.map(g => ({
        label: g.name,
        color: g.color,
        points: weeks.map((_, i) => {
          let pass = 0; let fail = 0
          for (let k = Math.max(0, i - 3); k <= i; k += 1) {
            pass += seriesSum(g.weekly, 'Pass', k)
            fail += seriesSum(g.weekly, 'Fail', k)
          }
          const tested = pass + fail
          return tested >= 3 ? Math.round((100 * pass) / tested) : null
        })
      }))
    })

    // status mix per group
    const statusColors = {
      Pass: 'var(--m-st-pass)', Fail: 'var(--m-st-fail)', Completed: 'var(--m-st-completed)', 'Not tested': 'var(--m-st-nt)', Other: 'var(--m-st-other)'
    }
    hBarsStacked(this.cmpMixTarget, groups.map(g => ({
      label: g.name,
      total: g.kpis.current.results,
      parts: Object.keys(statusColors).map(label => ({
        label,
        color: statusColors[label],
        value: weeks.reduce((a, _, i) => a + seriesSum(g.weekly, label, i), 0)
      }))
    })))
  }

  busyEl() { return this.element }

  // Guarded responses belonging to a superseded load() are dropped, so a
  // slow older request can't paint over a newer one. Picker fetches pass
  // guarded: false — they populate dropdowns, not cards.
  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    const req = fetch(url, { headers: { Accept: 'application/json' } })
    if (guarded) trackBusy(this.busyEl(), req)
    req
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => {
        if (guarded && seq !== (this.loadSeq || 0)) return
        onData(data)
      })
      .catch(() => {})
  }

  // ---- trait value filters: pick a trait, then its value search list ----

  traitState(traitId) {
    let st = this.traitStates.get(traitId)
    if (!st) {
      const opt = [...this.traitPickerTarget.options].find(o => o.value === traitId)
      st = {
        traitName: opt ? opt.textContent : traitId, terms: [], picks: [], excludes: [], termTotals: new Map()
      }
      this.traitStates.set(traitId, st)
    }
    return st
  }

  // Trait picker change: show the chosen trait's value list, open.
  traitChosen() {
    this.renderValueList(true)
  }

  renderValueList(open) {
    if (this.valueList) this.valueList.destroy()
    const traitId = this.traitPickerTarget.value
    if (!traitId) {
      this.valueList = createSearchList(this.valueSearchTarget, {
        label: 'Trait value', fetchOptions: () => Promise.resolve({ options: [] })
      })
      this.valueList.setDisabled(true, 'Pick a trait first')
      return
    }
    const existing = this.traitStates.get(traitId)
    const traitName = existing ? existing.traitName : this.traitPickerTarget.selectedOptions[0].textContent
    this.valueList = createSearchList(this.valueSearchTarget, {
      label: traitName,
      placeholder: 'Any value',
      searchPlaceholder: `Search ${traitName}`,
      nounOne: 'value',
      nounMany: 'values',
      countNoun: 'results',
      state: existing,
      termTotals: existing ? existing.termTotals : undefined,
      fetchOptions: (q, state) => this.fetchTraitValueOptions(traitId, q, state),
      onApply: state => this.setTraitState(traitId, state)
    })
    if (open) this.valueList.open()
  }

  // Chip body click: bring that trait's list up.
  openTrait(traitId) {
    this.traitPickerTarget.value = traitId
    this.renderValueList(true)
  }

  setTraitState(traitId, state) {
    const st = this.traitState(traitId)
    if (!st.termTotals.size && this.valueList && this.traitPickerTarget.value === traitId) {
      this.valueList.termTotals.forEach((n, t) => st.termTotals.set(t, n))
    }
    Object.assign(st, { terms: state.terms, picks: state.picks, excludes: state.excludes })
    if (isEmptyState(st)) this.traitStates.delete(traitId)
    if (this.valueList && this.traitPickerTarget.value === traitId) this.valueList.setState(st)
    this.renderChips()
    this.load()
  }

  // Hot spot click: one more exact value of that trait.
  pushTraitFilter({ traitId, traitName, value }) {
    const id = String(traitId)
    const st = this.traitState(id)
    if (traitName) st.traitName = traitName
    this.setTraitState(id, { ...st, picks: st.picks.includes(value) ? st.picks : st.picks.concat([value]) })
  }

  // ---- job-level filter chips (tag / job column / job field) — scope
  // the jobs whose results are counted; server-side twin of the Jobs tab
  // chips ----

  // Job filter picker change: that source's value search list, open.
  jobFilterChosen() {
    this.jobChips.sourceChosen()
  }

  appendJobChipParams(p) {
    this.jobChips.appendParams(p)
  }

  // Values of one trait (or one tag/column/field source) match any of
  // them server-side, so they share a chip: "CPC Part #: A or B".
  renderChips() {
    const host = this.chipsTarget
    host.innerHTML = ''
    host.classList.add('m-sl-chips')
    renderFilterChips(host, this.taskList.chips())
    this.traitStates.forEach((st, traitId) => {
      renderFilterChips(host, fieldChips(st, {
        label: st.traitName,
        nounOne: 'value',
        nounMany: 'values',
        termTotals: st.termTotals,
        onChange: state => this.setTraitState(traitId, state),
        onOpen: () => this.openTrait(traitId)
      }))
    })
    renderFilterChips(host, this.jobChips.chips())
  }

  // ---- task exclusions (By task card checkboxes) ----

  taskLabel(key) {
    return this.taskLabels.get(key) || key
  }

  // By task checkbox: the same exclude_task[] state as the Task search
  // list's unchecked rows. Re-including a name under an exact-pick filter
  // (no term covering it) picks it, as the search list does.
  setTaskExcluded(key, excluded) {
    const state = this.taskList.getState()
    const has = state.excludes.includes(key)
    if (excluded === has) return
    if (excluded) {
      state.excludes = state.excludes.concat([key])
      state.picks = state.picks.filter(k => k !== key)
    } else {
      state.excludes = state.excludes.filter(k => k !== key)
      if (state.picks.length && !state.terms.some(t => matchesTerm(key, t))) state.picks.push(key)
    }
    this.taskList.apply(state)
  }

  // ---- cards ----

  renderKpis(data) {
    this.renderRangeNote(data)
    const cur = data.current
    const prev = data.previous
    const pct = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null)
    const resultsDelta = pct(cur.results, prev.results)
    kpiTile(this.kpiResultsTarget, {
      label: 'Results',
      value: fmtNum(cur.results),
      delta: resultsDelta,
      deltaText: resultsDelta === null ? null : `${Math.abs(resultsDelta)}%`,
      sub: 'one per task × sample'
    })
    const prDelta = (cur.pass_pct !== null && prev.pass_pct !== null) ? cur.pass_pct - prev.pass_pct : null
    kpiTile(this.kpiPassRateTarget, {
      label: 'Pass rate',
      value: cur.pass_pct === null ? '—' : `${cur.pass_pct}%`,
      delta: prDelta,
      deltaText: prDelta === null ? null : `${Math.abs(prDelta)} pts`,
      sub: `of ${fmtNum(cur.tested)} tested results`
    })
    const failDelta = pct(cur.fails, prev.fails)
    kpiTile(this.kpiFailsTarget, {
      label: 'Failures',
      value: fmtNum(cur.fails),
      delta: failDelta,
      deltaText: failDelta === null ? null : `${Math.abs(failDelta)}%`,
      upIsGood: false
    })
    kpiTile(this.kpiLastTarget, {
      label: 'Last result',
      value: data.last ? this.relative(data.last.at) : '—',
      delta: null,
      deltaText: null,
      sub: data.last ? `${data.last.job} · ${data.last.task}`.slice(0, 60) : 'No matching results'
    })
  }

  relative(iso) {
    const days = Math.round((Date.now() - new Date(iso).getTime()) / 86400000)
    if (days <= 0) return 'today'
    if (days === 1) return 'yesterday'
    return `${days} d ago`
  }

  renderWeekly(data) {
    const series = stackedColumns(this.weeklyChartTarget, data, { colorMap: STATUS_COLORS })
    const legend = this.weeklyLegendTarget
    legend.innerHTML = ''
    if (series && series.length > 1) {
      series.forEach((s) => {
        const key = document.createElement('span')
        key.className = 'm-key'
        const sw = document.createElement('span')
        sw.className = 'm-swatch'
        sw.style.background = s.color
        key.appendChild(sw)
        key.appendChild(document.createTextNode(s.label))
        legend.appendChild(key)
      })
    }
    renderTable(
      this.weeklyTableTarget,
      [{ label: 'Week' }].concat(data.series.map(s => ({ label: s.label, num: true }))),
      data.weeks.map((w, i) => [`Week of ${fmtDate(w)}`].concat(data.series.map(s => fmtNum(s.values[i] || 0)))),
    )
  }

  renderHotSpots(data) {
    this.hotSubTarget.textContent = `Highest fail rate · min ${data.min_tested} tested${
      data.overall_fail_pct === null ? '' : ` · overall ${data.overall_fail_pct}%`
    } · click to add as a filter`
    hBars(this.hotChartTarget, data.rows.map(r => ({
      label: `${r.trait}: ${r.value}`,
      value: r.fail_pct,
      tipExtra: `${fmtNum(r.fails)} of ${fmtNum(r.tested)} tested failed`,
      traitId: r.trait_key,
      traitName: r.trait,
      rawValue: r.value
    })), {
      color: 'var(--m-st-fail)',
      labelW: 190,
      unitLabel: 'fail rate',
      fmt: v => `${v}%`,
      clickHint: 'Click to filter to this trait value',
      onClick: row => this.pushTraitFilter({ traitId: row.traitId, traitName: row.traitName, value: row.rawValue })
    })
    renderTable(
      this.hotTableTarget,
      [{ label: 'Trait value' }, { label: 'Fail rate', num: true }, { label: 'Failed / tested', num: true }],
      data.rows.map(r => [`${r.trait}: ${r.value}`, `${r.fail_pct}%`, `${fmtNum(r.fails)} / ${fmtNum(r.tested)}`]),
    )
  }

  // Included rows first (server order, most results first), then the
  // excluded names, dimmed and unchecked: they no longer come back from
  // by_task, so they render from this.excludedTasks. The checkbox toggles
  // the exclusion; a click elsewhere on an included row narrows the Task
  // picker to that name.
  renderByTask(data) {
    this.lastByTask = data
    const host = this.byTaskTarget
    host.innerHTML = ''
    data.rows.forEach(r => this.taskLabels.set(r.task_key, r.name))
    const excluded = new Set(this.excludedTasks)
    const rows = data.rows.filter(r => !excluded.has(r.task_key))
    if (!rows.length && !excluded.size) {
      const empty = document.createElement('div')
      empty.className = 'm-empty'
      empty.textContent = 'No results for these filters.'
      host.appendChild(empty)
      return
    }
    const table = document.createElement('table')
    table.className = 'm-table m-tx-table'
    const thead = document.createElement('thead')
    const trh = document.createElement('tr')
    const cols = [
      { label: '', cls: 'm-tx-check' }, { label: 'Task' }, { label: 'Results', cls: 'num' },
      { label: 'Last result', cls: 'num' }, { label: 'Pass rate' }
    ]
    cols.forEach((c) => {
      const th = document.createElement('th')
      th.textContent = c.label
      if (c.cls) th.className = c.cls
      trh.appendChild(th)
    })
    thead.appendChild(trh)
    table.appendChild(thead)
    const tbody = document.createElement('tbody')
    const checkCell = (tr, key, name, checked) => {
      const td = document.createElement('td')
      td.className = 'm-tx-check'
      const box = document.createElement('input')
      box.type = 'checkbox'
      box.checked = checked
      box.title = checked ? 'Uncheck to leave this task out of every card' : 'Check to count this task again'
      box.setAttribute('aria-label', `Include ${name}`)
      box.addEventListener('click', ev => ev.stopPropagation())
      box.addEventListener('change', () => this.setTaskExcluded(key, !box.checked))
      td.appendChild(box)
      tr.appendChild(td)
    }
    const cell = (tr, text, cls) => {
      const td = document.createElement('td')
      td.textContent = text
      if (cls) td.className = cls
      tr.appendChild(td)
      return td
    }
    rows.forEach((r) => {
      const tr = document.createElement('tr')
      checkCell(tr, r.task_key, r.name, true)
      cell(tr, r.name, 'm-tx-name').title = r.name
      cell(tr, fmtNum(r.results), 'num')
      cell(tr, fmtDate(r.last_at), 'num')
      const td = document.createElement('td')
      if (r.pass_pct === null) {
        td.textContent = '—'
      } else {
        const meter = document.createElement('span')
        meter.className = 'm-meter'
        const track = document.createElement('span')
        track.className = 'm-meter-track'
        const fill = document.createElement('span')
        fill.className = `m-meter-fill${r.pass_pct < 80 ? ' low' : ''}`
        fill.style.width = `${r.pass_pct}%`
        track.appendChild(fill)
        meter.appendChild(track)
        const pctLabel = document.createElement('span')
        pctLabel.textContent = `${r.pass_pct}%`
        meter.appendChild(pctLabel)
        td.appendChild(meter)
      }
      tr.appendChild(td)
      tr.className = 'm-rowlink'
      tr.title = 'Show only this task'
      tr.addEventListener('click', () => {
        this.taskList.apply({ terms: [], picks: [r.task_key], excludes: this.excludedTasks })
      })
      tbody.appendChild(tr)
    })
    const sortedExcluded = [...excluded].sort((a, b) => this.taskLabel(a).localeCompare(this.taskLabel(b)))
    sortedExcluded.forEach((key) => {
      const tr = document.createElement('tr')
      tr.className = 'm-tx-off'
      tr.title = 'Excluded from every card'
      checkCell(tr, key, this.taskLabel(key), false)
      cell(tr, this.taskLabel(key), 'm-tx-name')
      cell(tr, '—', 'num')
      cell(tr, '—', 'num')
      cell(tr, 'Excluded', 'm-tx-note')
      tbody.appendChild(tr)
    })
    table.appendChild(tbody)
    host.appendChild(table)
  }

  toggleTable(ev) {
    const btn = ev.currentTarget
    const card = btn.closest('.m-card')
    const showing = card.classList.toggle('show-table')
    btn.textContent = showing ? 'Chart' : 'Table'
  }
}
